/**
 * DivisionPicker - Wrapper around generic EntityPicker
 * 
 * Provides division-specific configuration and maintains backward compatibility
 * with any code that imports DivisionPickerField or DivisionPickerModal
 */

import React from 'react';
import { EntityPickerField } from '../EntityPicker/EntityPickerField';
import { EntityPickerModal } from '../EntityPicker/EntityPickerModal';
import { EntityPickerSelect } from '../EntityPicker/EntityPickerSelect';

// ── Division-specific configuration ──────────────────────────────────────────
export const DIVISION_CONFIG = {
  apiEndpoint: "/api/Division/active",
  entityName: "Division",
  idField: "id",
  displayFields: {
    primary: "description",
    secondary: "code"
  },
  tableHeaders: ["Code", "Description"],
  tableAccessors: ["code", "description"],
  searchFields: [
    { label: "All", field: "all", index: 0 },
    { label: "Description", field: "description", index: 1 },
    { label: "Code", field: "code", index: 2 },
  ],
  placeholder: "--Select division--"
};

// ── DivisionPickerSelect wrapper ─────────────────────────────────────────────
/**
 * DivisionPickerSelect
 * 
 * Usage:
 * <DivisionPickerSelect
 *   label="Division"
 *   value={form.divisionId}
 *   onSelect={(val) => setDivision(val)}
 *   placeholder="All Divisions"
 * />
 */
export function DivisionPickerSelect({
  label,
  value,   // The ID value
  onSelect,
  onClear,
  placeholder = "--Select division--",
  allowAll,
  disabled = false,
  isRequired = false,
  className = "",
  error = false,
  errorMessage = ""
}) {
  return (
    <div className="mb-1 w-full">
      {label && (
        <label className="mb-1.5 block text-sm font-medium text-gray-600">
          {label}
          {isRequired && <span className="ml-1 text-red-500">*</span>}
        </label>
      )}

      <div className={error ? "rounded-md ring-1 ring-red-500" : ""}>
        <EntityPickerSelect
          config={DIVISION_CONFIG}
          value={value ? { id: value } : null}
          disabled={disabled}
          placeholder={placeholder}
          allowAll={allowAll}
          className={className}
          onChange={(val) => {
            if (val === null) {
              if (onClear) onClear();
            } else {
              // Returns the full object {id, code, description}
              if (onSelect) onSelect(val);
            }
          }}
        />
      </div>

      {/* Error message display */}
      {error && (
        <span className="mt-1 block text-sm text-red-500">
          {errorMessage}
        </span>
      )}
    </div>
  );
}

// ── DivisionPickerField wrapper ──────────────────────────────────────────────
/**
 * DivisionPickerField
 */
export function DivisionPickerField({
  divisionId,       // division-specific prop name
  displayValue = "",
  onSelect,
  onClear,
  ...rest             // label, isRequired, error, disabled, etc.
}) {
  return (
    <EntityPickerField
      {...rest}
      config={DIVISION_CONFIG}
      entityId={divisionId}
      displayValue={displayValue}
      onSelect={onSelect}
      onClear={onClear}
    />
  );
}

// ── DivisionPickerModal wrapper ──────────────────────────────────────────────
/**
 * DivisionPickerModal
 */
export function DivisionPickerModal({ isOpen, onClose, setData }) {
  return (
    <EntityPickerModal
      isOpen={isOpen}
      onClose={onClose}
      setData={setData}
      config={DIVISION_CONFIG}
    />
  );
}

// ── Exports (backward compatibility) ─────────────────────────────────────────
export { DivisionPickerField as default };